import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Rocket, House, Toolbox, ShieldCheck, Sparkle, CaretRight,
  Star, ChatCircle, CurrencyGbp, Eye,
} from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/Logo";

/**
 * /demo — public demo landing page. Two big buttons that auto-log the visitor
 * in as a pre-seeded demo trader or demo customer. Perfect for outreach:
 * paste the /demo link in WhatsApp and prospects can explore without signing up.
 */
export default function Demo() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(null);

  const enterAs = async (role) => {
    setLoading(role);
    try {
      const { data } = await api.post(`/demo/login-as/${role}`);
      setUser(data.user);
      // fire-and-forget funnel event
      try { api.post("/demo/event", { kind: `demo_started_${role}` }); } catch (_) { /* noop */ }
      toast.success(data.message || "Demo mode active", { duration: 5000 });
      nav(role === "trader" ? "/dashboard/trader" : "/dashboard/customer");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-sand)" }}>
      {/* Announcement bar */}
      <div
        className="w-full text-center text-[12px] sm:text-[13px] font-semibold tracking-wide py-2 px-3"
        style={{ background: "var(--brand-green-dark)", color: "#F4FDF7" }}
        data-testid="demo-announcement-bar"
      >
        <span className="inline-flex items-center gap-2">
          <Eye size={14} weight="fill" style={{ color: "#FFB073" }} />
          Live Demo · No sign-up needed · Fake data only
        </span>
      </div>

      {/* Header */}
      <header className="px-4 sm:px-8 py-3 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" data-testid="demo-brand-link">
          <Logo size={72} />
        </Link>
        <Link
          to="/"
          className="text-sm font-semibold opacity-80 hover:opacity-100"
          style={{ color: "var(--brand-green)" }}
          data-testid="demo-back-to-waitlist"
        >
          ← Back to waitlist
        </Link>
      </header>

      {/* Hero */}
      <section className="px-4 sm:px-8 pt-4 pb-10 max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-4"
             style={{ color: "var(--brand-orange)" }}>
          <Sparkle size={14} weight="fill" /> Try MyHandyHub — with fake data
        </div>
        <h1
          className="font-display font-extrabold leading-[1.05] tracking-tight"
          style={{ color: "var(--brand-green)", fontSize: "clamp(2.2rem, 6vw, 4rem)" }}
          data-testid="demo-hero-heading"
        >
          Take MyHandyHub<br className="hidden sm:block" /> for a spin.
        </h1>
        <p className="mt-5 text-[15px] sm:text-lg text-[#4A5D54] max-w-2xl mx-auto leading-relaxed">
          Explore the full working platform — with a realistic pipeline of{" "}
          <strong>8 tradespeople</strong>, <strong>5 customers</strong>, and{" "}
          <strong>12 live jobs</strong> already loaded. Click, tap, quote — nothing you do here
          affects real users.
        </p>

        {/* Two big role buttons */}
        <div className="mt-9 grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
          <button
            onClick={() => enterAs("trader")}
            disabled={loading !== null}
            className="group px-5 py-5 rounded-2xl font-bold flex items-start gap-4 shadow-lg shadow-orange-900/10 hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
            style={{ background: "var(--brand-orange)", color: "#fff" }}
            data-testid="demo-enter-trader"
          >
            <span
              className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.92)" }}
              aria-hidden
            >
              <Toolbox size={30} weight="duotone" style={{ color: "var(--brand-orange)" }} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center justify-between gap-2">
                <span className="text-lg">Try as a Tradesperson</span>
                <CaretRight size={18} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </span>
              <span className="block text-xs opacity-90 font-medium mt-1.5 leading-snug">
                {loading === "trader" ? "Loading…" : "See your job alerts, quote flow, messages, and dashboard."}
              </span>
            </span>
          </button>

          <button
            onClick={() => enterAs("customer")}
            disabled={loading !== null}
            className="group px-5 py-5 rounded-2xl font-bold flex items-start gap-4 shadow-lg shadow-emerald-900/10 hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
            style={{ background: "var(--brand-green)", color: "#fff" }}
            data-testid="demo-enter-customer"
          >
            <span
              className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.92)" }}
              aria-hidden
            >
              <House size={30} weight="duotone" style={{ color: "var(--brand-green)" }} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center justify-between gap-2">
                <span className="text-lg">Try as a Customer</span>
                <CaretRight size={18} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </span>
              <span className="block text-xs opacity-90 font-medium mt-1.5 leading-snug">
                {loading === "customer" ? "Loading…" : "Post a job, browse trades, review quotes."}
              </span>
            </span>
          </button>
        </div>

        <p className="mt-6 text-xs text-[#7A8B82] max-w-xl mx-auto">
          You'll be logged in automatically — no account, no signup. A red banner at the top
          of every page reminds you that you're in demo mode.
        </p>
      </section>

      {/* What you'll see */}
      <section className="px-4 sm:px-8 pb-16 max-w-5xl mx-auto">
        <h2 className="text-center text-sm sm:text-base font-extrabold tracking-[0.18em] uppercase mb-8"
            style={{ color: "var(--brand-green)" }}>
          What you'll see inside
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <FeatureTile Icon={Rocket} title="Live job alerts" body="Every relevant job in your area — trade, distance, budget — arrives instantly." accent />
          <FeatureTile Icon={CurrencyGbp} title="Quote flow" body="Send a formal quote, track its status, chat before you commit." />
          <FeatureTile Icon={ChatCircle} title="Built-in messaging" body="No need to share phone numbers until you're ready to book." />
          <FeatureTile Icon={Star} title="Reviews & badge" body="Verified ID, insurance, and public star ratings after each job." accent />
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-4 sm:px-8 pb-16 max-w-3xl mx-auto text-center">
        <div
          className="rounded-3xl p-7 sm:p-10 border"
          style={{ background: "#fff", borderColor: "#CBE7D6" }}
        >
          <ShieldCheck size={32} weight="fill" style={{ color: "var(--brand-green)" }} className="mx-auto" />
          <p className="mt-3 text-lg sm:text-xl font-display font-bold" style={{ color: "var(--brand-green)" }}>
            Like what you see?
          </p>
          <p className="mt-2 text-sm text-[#4A5D54]">
            Join the waiting list for early access. £3.99/month for the first 12 months when we go live.
          </p>
          <Link
            to="/"
            onClick={() => { try { api.post("/demo/event", { kind: "demo_clicked_join_waitlist" }); } catch (_) { /* noop */ } }}
            className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-xl font-bold shadow-lg shadow-emerald-900/10 hover:shadow-xl transition-all hover:-translate-y-0.5"
            style={{ background: "var(--brand-green)", color: "#fff" }}
            data-testid="demo-join-waitlist"
          >
            <Rocket size={18} weight="fill" />
            Join the waiting list
          </Link>
        </div>
      </section>
    </div>
  );
}

function FeatureTile({ Icon, title, body, accent }) {
  return (
    <div
      className="rounded-2xl p-5 border bg-white text-left"
      style={{ borderColor: accent ? "#FFB073" : "#E4E2DC" }}
      data-testid="demo-feature-tile"
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
        style={{ background: accent ? "#FFF1E5" : "#F4FDF7", color: accent ? "#E26D29" : "#11523B" }}
      >
        <Icon size={22} weight="duotone" />
      </div>
      <div className="font-display font-extrabold text-base" style={{ color: "var(--brand-green)" }}>
        {title}
      </div>
      <p className="mt-1 text-sm text-[#4A5D54] leading-relaxed">{body}</p>
    </div>
  );
}
